import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { listTeachings } from "@/lib/teachings.functions";
import { listParashiot } from "@/lib/parashiot.functions";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, CalendarDays, Sun, Sunset } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/calendario")({
  component: CalendarioPage,
  head: () => ({
    meta: [
      { title: "Calendário de Ensinos | Beit Tefilah Rechovot" },
      {
        name: "description",
        content:
          "Navegue pelos ensinos da congregação Beit Tefilah Rechovot por data e por Parashat.",
      },
    ],
  }),
});

const MONTHS_PT = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];
const WEEKDAYS_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function toISO(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function CalendarioPage() {
  const navigate = useNavigate();
  const listFn = useServerFn(listTeachings);
  const parashiotFn = useServerFn(listParashiot);

  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);
  const dateFrom = toISO(firstOfMonth);
  const dateTo = toISO(lastOfMonth);

  const { data, isLoading } = useQuery({
    queryKey: ["calendar-teachings", dateFrom, dateTo],
    queryFn: () => listFn({ data: { dateFrom, dateTo, limit: 100, order: "oldest" } }),
  });

  const { data: parashiot } = useQuery({
    queryKey: ["parashiot"],
    queryFn: () => parashiotFn(),
  });

  // group teachings by date
  const byDate = useMemo(() => {
    const map = new Map<string, any[]>();
    (data?.rows ?? []).forEach((t: any) => {
      if (!t.teaching_date) return;
      const key = t.teaching_date;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    });
    return map;
  }, [data]);

  // build calendar cells
  const cells = useMemo(() => {
    const startWeekday = firstOfMonth.getDay(); // 0=Sun
    const daysInMonth = lastOfMonth.getDate();
    const arr: Array<{ date: Date | null; iso: string | null }> = [];
    for (let i = 0; i < startWeekday; i++) arr.push({ date: null, iso: null });
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(year, month, d);
      arr.push({ date: dt, iso: toISO(dt) });
    }
    while (arr.length % 7 !== 0) arr.push({ date: null, iso: null });
    return arr;
  }, [year, month]);

  const goPrev = () => {
    setCursor(new Date(year, month - 1, 1));
    setSelectedDay(null);
  };
  const goNext = () => {
    setCursor(new Date(year, month + 1, 1));
    setSelectedDay(null);
  };
  const goToday = () => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDay(toISO(today));
  };

  const selectedTeachings = selectedDay ? (byDate.get(selectedDay) ?? []) : [];

  const yearOptions: number[] = [];
  for (let y = today.getFullYear() + 1; y >= 2015; y--) yearOptions.push(y);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-primary sm:text-3xl">
            Calendário de Ensinos
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Navegue pelas datas e Parashiot para encontrar ensinos rapidamente.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select onValueChange={(slug) => navigate({ to: "/parashiot/$slug", params: { slug } })}>
            <SelectTrigger aria-label="Ir para uma Parashá" className="w-full sm:w-64">
              <SelectValue placeholder="Ir para Parashat..." />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {(parashiot ?? []).map((p: any) => (
                <SelectItem key={p.id} value={p.slug}>
                  {p.number}. {p.name_translit}
                  {p.teachingCount ? ` (${p.teachingCount})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-3 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={goPrev} aria-label="Mês anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={goNext} aria-label="Próximo mês">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={goToday}>
              Hoje
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={String(month)}
              onValueChange={(v) => setCursor(new Date(year, Number(v), 1))}
            >
              <SelectTrigger aria-label="Mês" className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS_PT.map((m, i) => (
                  <SelectItem key={m} value={String(i)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(year)}
              onValueChange={(v) => setCursor(new Date(Number(v), month, 1))}
            >
              <SelectTrigger aria-label="Ano" className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
          {WEEKDAYS_PT.map((d) => (
            <div key={d} className="py-2">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (!c.date || !c.iso) return <div key={i} className="aspect-square" />;
            const items = byDate.get(c.iso) ?? [];
            const has = items.length > 0;
            const isToday = c.iso === toISO(today);
            const isSelected = c.iso === selectedDay;
            const isSaturday = c.date.getDay() === 6;
            return (
              <button
                key={i}
                onClick={() => has && setSelectedDay(c.iso)}
                disabled={!has}
                className={cn(
                  "relative flex aspect-square flex-col items-center justify-start rounded-lg border p-1 text-xs transition-colors sm:p-2 sm:text-sm",
                  has
                    ? "cursor-pointer border-primary/30 bg-primary/5 hover:bg-primary/10"
                    : "border-transparent text-muted-foreground/60",
                  isSaturday && !has && "bg-secondary/40",
                  isSelected &&
                    "border-primary bg-primary text-primary-foreground hover:bg-primary",
                  isToday && !isSelected && "ring-1 ring-primary",
                )}
              >
                <span className={cn("font-medium", isSelected && "text-primary-foreground")}>
                  {c.date.getDate()}
                </span>
                {has && (
                  <span className="mt-auto flex items-center gap-0.5">
                    {items.length > 1 ? (
                      <span
                        className={cn(
                          "rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                          isSelected
                            ? "bg-primary-foreground text-primary"
                            : "bg-primary text-primary-foreground",
                        )}
                      >
                        {items.length}
                      </span>
                    ) : (
                      <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          isSelected ? "bg-primary-foreground" : "bg-primary",
                        )}
                      />
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {isLoading && (
          <div className="mt-3 text-center text-xs text-muted-foreground">Carregando...</div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-4 sm:p-5">
        <div className="mb-3 flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          <h2 className="font-serif text-lg font-semibold text-primary">
            {selectedDay
              ? formatDateBR(selectedDay, {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })
              : "Selecione um dia com ensinos"}
          </h2>
        </div>

        {selectedDay && selectedTeachings.length === 0 && (
          <p className="text-sm text-muted-foreground">Nenhum ensino nesta data.</p>
        )}

        <div className="space-y-2">
          {selectedTeachings.map((t: any) => (
            <Link
              key={t.id}
              to="/ensinos/$id"
              params={{ id: t.id }}
              className="flex items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:border-primary/40 hover:bg-secondary/40"
            >
              {t.thumbnail_url && (
                <img
                  src={t.thumbnail_url}
                  alt=""
                  className="h-16 w-28 shrink-0 rounded object-cover sm:h-20 sm:w-32"
                  loading="lazy"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="line-clamp-2 font-medium text-foreground">
                  {t.custom_title?.trim() || t.title}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {t.turno === "manha" ? (
                    <span className="inline-flex items-center gap-1">
                      <Sun className="h-3 w-3" /> Manhã (11h)
                    </span>
                  ) : t.turno === "tarde" ? (
                    <span className="inline-flex items-center gap-1">
                      <Sunset className="h-3 w-3" /> Tarde (15h30)
                    </span>
                  ) : null}
                  {t.parashiot && (
                    <span>
                      Parashat {t.parashiot.name_translit}
                      {t.parashiot.number ? ` (${t.parashiot.number})` : ""}
                    </span>
                  )}
                  {t.hebrew_date && <span>{t.hebrew_date}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
